import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiSettingsDirs from 'api/apiSettingsDirs';
import type { ApiKeyType, ApiKeyTypeRow } from '../../types';
import { SETTINGS_DIR_NAME_MAX_LENGTH } from '../../types';
import type { ApiKeyFormState } from '../uiTypes';

const createEmptyForm = (): ApiKeyFormState => ({
    keyName: '',
    apiKey: '',
    accessJson: [],
    eventTypesJson: [],
    webhookUrl: '',
    allowedDomains: '',
    isActive: 1,
});

const createRandomApiKey = (): string => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const chars = Array.from(crypto.getRandomValues(new Uint8Array(40))).map((n) => alphabet[n % alphabet.length]);
    return `ek_${chars.join('')}`;
};

const useApiKeys = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { showToast } = useToast();
    const [rows, setRows] = useState<ApiKeyTypeRow[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [savingType, setSavingType] = useState<ApiKeyType | null>(null);
    const [tab, setTab] = useState<ApiKeyType>('in');
    const [showCreateIn, setShowCreateIn] = useState(false);
    const [showCreateOut, setShowCreateOut] = useState(false);

    const [formIn, setFormIn] = useState<ApiKeyFormState>(createEmptyForm());
    const [formOut, setFormOut] = useState<ApiKeyFormState>(createEmptyForm());
    const [editingInId, setEditingInId] = useState<number | null>(null);
    const [editingOutId, setEditingOutId] = useState<number | null>(null);

    const rowsIn = (rows || []).filter((r) => r.keyType === 'in');
    const rowsOut = (rows || []).filter((r) => r.keyType === 'out');

    const fetchRows = async () => {
        setIsLoading(true);
        const result = await apiSettingsDirs.getApiKeys();
        setIsLoading(false);
        if (!result) return showToast(t('common:messError'), 'danger');
        setRows(result);
    };

    useEffect(() => {
        fetchRows();
    }, []);

    const getForm = (type: ApiKeyType) => (type === 'in' ? formIn : formOut);
    const setForm = (type: ApiKeyType, form: ApiKeyFormState) => (type === 'in' ? setFormIn(form) : setFormOut(form));
    const getEditingId = (type: ApiKeyType) => (type === 'in' ? editingInId : editingOutId);
    const setEditingId = (type: ApiKeyType, value: number | null) =>
        type === 'in' ? setEditingInId(value) : setEditingOutId(value);
    const getShowCreate = (type: ApiKeyType) => (type === 'in' ? showCreateIn : showCreateOut);
    const setShowCreate = (type: ApiKeyType, value: boolean) =>
        type === 'in' ? setShowCreateIn(value) : setShowCreateOut(value);

    const validateForm = (type: ApiKeyType, form: ApiKeyFormState): string | null => {
        if (!String(form.keyName || '').trim()) return t('apiKeys.errors.nameRequired');
        if (String(form.keyName || '').trim().length > SETTINGS_DIR_NAME_MAX_LENGTH) {
            return t('apiKeys.errors.nameTooLong', { max: SETTINGS_DIR_NAME_MAX_LENGTH });
        }
        if (!String(form.apiKey || '').trim()) return t('apiKeys.errors.keyRequired');
        if (type === 'in') {
            if (form.accessJson.length === 0) return t('apiKeys.errors.accessRequired');
        }
        if (type === 'out') {
            if (!String(form.webhookUrl || '').trim()) return t('apiKeys.errors.urlRequired');
            if (!/^https?:\/\//i.test(form.webhookUrl)) return t('apiKeys.errors.urlInvalid');
            if (form.eventTypesJson.length === 0) return t('apiKeys.errors.eventRequired');
        }
        return null;
    };

    const saveForm = async (type: ApiKeyType) => {
        const form = getForm(type);
        const editId = getEditingId(type);
        const err = validateForm(type, form);
        if (err) return showToast(err, 'danger');

        setSavingType(type);
        const payload = {
            keyName: form.keyName.trim(),
            keyType: type,
            apiKey: form.apiKey.trim(),
            accessJson: type === 'in' ? form.accessJson : [],
            eventTypesJson: type === 'out' ? form.eventTypesJson : [],
            webhookUrl: type === 'out' ? form.webhookUrl.trim() : '',
            allowedDomains: type === 'in' ? form.allowedDomains.trim() : '',
            isActive: form.isActive,
            deleted: 0,
        };
        const result = editId
            ? await apiSettingsDirs.updateApiKey(editId, payload)
            : await apiSettingsDirs.createApiKey(payload);
        setSavingType(null);

        if (!result?.stat) return showToast(t('apiKeys.errors.saveFailed'), 'danger');
        showToast(t(editId ? 'apiKeys.toasts.updated' : 'apiKeys.toasts.created'), 'success');
        setEditingId(type, null);
        setShowCreate(type, false);
        setForm(type, createEmptyForm());
        await fetchRows();
    };

    const deleteRow = async (row: ApiKeyTypeRow) => {
        if (!window.confirm(t('apiKeys.confirmDelete', { name: row.keyName }))) return;
        const result = await apiSettingsDirs.deleteApiKey(row.ID);
        if (!result?.stat) return showToast(t('apiKeys.errors.deleteFailed'), 'danger');
        showToast(t('apiKeys.toasts.deleted'), 'success');
        await fetchRows();
    };

    const startEdit = (row: ApiKeyTypeRow) => {
        setEditingId(row.keyType, row.ID);
        setShowCreate(row.keyType, true);
        setForm(row.keyType, {
            keyName: row.keyName || '',
            apiKey: row.apiKey || '',
            accessJson: Array.isArray(row.accessJson) ? row.accessJson : [],
            eventTypesJson: Array.isArray(row.eventTypesJson) ? row.eventTypesJson : [],
            webhookUrl: row.webhookUrl || '',
            allowedDomains: row.allowedDomains || '',
            isActive: Number(row.isActive) ? 1 : 0,
        });
        setTab(row.keyType);
    };

    const resetForm = (type: ApiKeyType) => {
        setEditingId(type, null);
        setShowCreate(type, false);
        setForm(type, createEmptyForm());
    };

    const generateFormKey = (type: ApiKeyType) => {
        setForm(type, { ...getForm(type), apiKey: createRandomApiKey() });
    };

    const openCreateForm = (type: ApiKeyType) => {
        setEditingId(type, null);
        setShowCreate(type, true);
        setForm(type, {
            ...createEmptyForm(),
            apiKey: createRandomApiKey(),
        });
    };

    return {
        tab,
        setTab,
        rows,
        rowsIn,
        rowsOut,
        isLoading,
        formIn,
        formOut,
        editingInId,
        editingOutId,
        savingType,
        getShowCreate,
        openCreateForm,
        setForm,
        resetForm,
        saveForm,
        startEdit,
        deleteRow,
        generateFormKey,
    };
};

export default useApiKeys;
